import { chatMentionableUsers, documentMentionableUsers } from "@/api";
import { debouncedPromise, SupersededError } from "@/utils/debouncedPromise";

import type {
  MentionSuggestion,
  ProviderSearchOptions,
  SuggestionProvider,
} from "@/components/suggestions/types";

/** Exported so the chip's Backspace can collapse a mention back to what typed it. */
export const MENTION_TRIGGER = "@";

// Short enough that the picker still feels immediate at one character.
const SEARCH_DEBOUNCE_MS = 150;

type MentionScope = NonNullable<ProviderSearchOptions["mention"]>["scope"];

async function requestMentionableUsers(
  scope: MentionScope,
  query: string,
  signal?: AbortSignal,
): Promise<MentionSuggestion[]> {
  const { users } =
    "chatUid" in scope
      ? await chatMentionableUsers(scope.chatUid, { query }, { signal })
      : await documentMentionableUsers(scope.documentUid, { query }, { signal });

  if (!users) return [];

  return users.map((user) => ({
    type: "mention",
    user,
    popupGroup: "mention",
    priority: 1,
  }));
}

const fetchMentionableUsers = debouncedPromise(requestMentionableUsers, SEARCH_DEBOUNCE_MS);

async function fetchMentionSuggestions(
  scope: MentionScope,
  query: string,
  signal?: AbortSignal,
): Promise<MentionSuggestion[]> {
  try {
    return await fetchMentionableUsers(scope, query, signal);
  } catch (error) {
    if (!(error instanceof SupersededError) && !signal?.aborted) console.error(error);
    return [];
  }
}

export const mentionSuggestionsProvider: SuggestionProvider<"mention"> = {
  type: "mention",
  search: (str, _doc, _lang, options, signal) => {
    if (!str.startsWith(MENTION_TRIGGER)) return [];

    const { scope } = options ?? {};

    if (!scope) return [];

    return [fetchMentionSuggestions(scope, str.slice(MENTION_TRIGGER.length), signal)];
  },
};
